import "./StripeCoreCheckoutPage.scss";

import { Alert, ExclamationTriangleIcon, Flex } from "@fluentui/react-northstar";
import { StandardPlanLevels, StripeSubType } from "@teams/types";
import { UserDTO } from "@teams/types/api";
import moment from "moment";
import React, { useMemo, useState } from "react";

import { useTheme } from "../../_shared/state/ThemeProvider";
import { getPlanType } from "../../_shared/utils/paymentHelpers";
import { getTrialExpiresAt } from "../../_shared/utils/userHelpers";
import { StripeCheckoutPageHeader } from "./StripeCheckoutPageHeader";
import { StripePaymentPage } from "./StripePaymentPage";
import { StripePlanSelectionPage } from "./StripePlanSelectionPage";

type props = {
  pricingPlans: StandardPlanLevels[];
  user: UserDTO;
  isTrial: boolean;
  authToken?: string;
  onCheckoutComplete: (success: boolean, trialEnd?: Date) => void;
};

export const StripeCoreCheckoutPage: React.FC<props> = ({
  pricingPlans,
  user,
  isTrial,
  authToken,
  onCheckoutComplete,
}) => {
  const { themeName } = useTheme();

  const [selectedPlan, setSelectedPlan] = useState<string>();
  const [trialMode, setTrialMode] = useState<boolean>(isTrial);

  const currentSubscription = useMemo(() => {
    return user.subscriptions.find(sub => sub.state === "active") ?? user.subscriptions[0];
  }, [user.subscriptions]);

  const isDarkTheme = useMemo(() => {
    return ["dark", "contrast"].includes(themeName);
  }, [themeName]);

  const classForDarkTheme = useMemo(() => {
    return isDarkTheme ? " dark" : "";
  }, [isDarkTheme]);

  const currentSubType: StripeSubType = useMemo(() => {
    if (!currentSubscription) {
      return "free";
    }
    if (currentSubscription.type === "trial") {
      if (
        currentSubscription.state === "ended" ||
        moment(currentSubscription.trialExpiresAt).isBefore()
      ) {
        return "trial-expired";
      }

      return "trial";
    } else {
      if (currentSubscription.state === "ended") {
        return "expired";
      }
      const planId = currentSubscription.providerSubscriptionMetadata?.planId;
      const planType = getPlanType(planId);
      if (planType === "Standard") {
        return "standard";
      } else if (planType === "Pro") {
        return "pro";
      } else {
        return "legacy";
      }
    }
  }, [currentSubscription]);

  const pageTitle = useMemo(() => {
    if (trialMode) {
      return `${user.featureFlags.trialDays} Day Free Trial`;
    }
    if (selectedPlan) {
      return "Payment details";
    }
    return "Choose a Polly plan";
  }, [selectedPlan, trialMode, user.featureFlags.trialDays]);

  const trialExpiresAt = useMemo(() => {
    if (currentSubscription && currentSubscription.type === "trial") {
      return getTrialExpiresAt(currentSubscription, user.featureFlags);
    } else return undefined;
  }, [currentSubscription, user.featureFlags]);

  if (user.subscriptions.some(s => s.state === "active" && s.type === "full")) {
    return (
      <Alert
        info
        icon={<ExclamationTriangleIcon />}
        content="You already have an active Polly subscription!"
      />
    );
  }

  return (
    <Flex column gap="gap.large" hAlign="center" className="stripe-container">
      <StripeCheckoutPageHeader
        pageTitle={pageTitle}
        selectedPage={selectedPlan ? "make-payment" : "choose-plan"}
        isDarkTheme={isDarkTheme}
        classForDarkTheme={classForDarkTheme}
        navigateToPlansPage={() => {
          setSelectedPlan(undefined);
        }}
      />
      {!selectedPlan ? (
        <StripePlanSelectionPage
          pricingPlans={pricingPlans}
          onPlanSelected={setSelectedPlan}
          isTrial={trialMode}
          setTrial={setTrialMode}
          currentSubscription={currentSubscription}
          currentSubType={currentSubType}
          user={user}
          isDarkTheme={isDarkTheme}
          classForDarkTheme={classForDarkTheme}
          trialExpiresAt={trialExpiresAt}
        />
      ) : (
        <StripePaymentPage
          selectedPlan={pricingPlans.find(plan => plan.id === selectedPlan)!}
          user={user}
          isTrial={trialMode}
          authToken={authToken}
          onCheckoutComplete={onCheckoutComplete}
          isDarkTheme={isDarkTheme}
          classForDarkTheme={classForDarkTheme}
        />
      )}
    </Flex>
  );
};
